/* eslint-disable array-callback-return */
// REACT IMPORTS
import React, { useEffect, useState } from "react";

import DocPreview from "components/ui-component/DocPreview";
import AudioPreview from "components/ui-component/AudioPreview";
import MessageToast from "components/ui-component/MessageToast";
import { useAppDispatch } from "store/hooks";
import { attachmentDownload } from "store/slices/documentsSlice";
import { downloadFileFromURL } from "utils/HelperFunctions";

// ICONS IMPORTS
import { CloseIcon } from "icons/payments/CloseIcon";
import { DownloadIcon } from "icons/payments/DownloadIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import { useTid } from "utils/testid";

interface PreviewDocumentProps {
  data: any;
  setData: any;
  setShow: any;
  setMsg: (val: any) => void;
  show?: boolean;
  isPreviewId?: string;
  setIsPreviewId?: any;
}

/* ------------------------------------- PAYMENT PREVIEW ------------------------------------- */

const PreviewDocuments = (props: PreviewDocumentProps) => {
  const { data, setData, setShow, isPreviewId, setIsPreviewId, setMsg } = props;
  // Plain id builder — safe to call inside conditional JSX, unlike the
  // useTestAttrs hook, which must not run conditionally.
  const tid = useTid();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [url, setUrl] = useState<string | null>("");

  const [isDownloadingAny, setIsDownloadingAny] = useState<boolean>(false);
  const [isHeaderLoading, setIsHeaderLoading] = useState<boolean>(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  const [isDownloadedPreview, setIsDownloadedPreview] =
    useState<boolean>(false);

  const [localToast, setLocalToast] = useState({
    variant: "",
    content: "",
    nonce: 0,
  });

  const onClose = () => {
    setData(null);
    setShow(false);
    setUrl(null);
    setIsPreviewId?.(null);
    if (setMsg) {
      setMsg((state: any) =>
        state && state.isFromPreview
          ? { ...state, isFromPreview: false, content: "" }
          : state
      );
    }
  };

  const dispatch = useAppDispatch();

  const onAttachmentDownload = async () => {
    setIsLoading(true);
    try {
      const payload = { fileName: data?.title };
      const res: any = await dispatch(attachmentDownload(payload)).unwrap();
      if (res && res?.body?.success) setUrl(res?.body?.data);
      else setUrl(null);
    } catch (error: any) {
      console.log("attachment download err : -->", error?.message);
      setUrl(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsDownloadedPreview(false);

    if (data) onAttachmentDownload();
    else setUrl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const downloadAttachment = async (
    path: string,
    fileName: string
  ): Promise<boolean> => {
    try {
      const payload = { fileName: path };
      const res: any = await dispatch(attachmentDownload(payload)).unwrap();
      if (res && res?.body?.success) {
        const result = await downloadFileFromURL(res?.body?.data, fileName);
        return Boolean(result);
      }
      return false;
    } catch (error: any) {
      console.log("attachment download err : -->", error?.message);
      return false;
    }
  };

  const onDownloadHeader = async (path: string, fileName: string) => {
    setIsDownloadedPreview(false);

    setIsDownloadingAny(true);
    setIsHeaderLoading(true);

    try {
      const ok = await downloadAttachment(path, fileName);

      if (ok) {
        setLocalToast({
          variant: "success",
          content: `Attachment ${fileName} was successfully downloaded`,
          nonce: Date.now(),
        });
      } else {
        setLocalToast({
          variant: "error",
          content: `Failed to download attachment ${fileName}`,
          nonce: Date.now(),
        });
      }
    } catch (error: any) {
      console.log("onDownloadHeader error:", error?.message);
      setLocalToast({
        variant: "error",
        content: `Failed to download attachment ${fileName}`,
        nonce: Date.now(),
      });
    } finally {
      setIsHeaderLoading(false);
      setIsDownloadingAny(false);
    }
  };

  const onDownloadPreview = async (path: string, fileName: string) => {
    setIsDownloadingAny(true);
    setIsPreviewLoading(true);
    setIsDownloadedPreview(false);

    try {
      const ok = await downloadAttachment(path, fileName);

      if (ok) {
        setIsDownloadedPreview(true);

        setTimeout(() => {
          setIsDownloadedPreview(false);
        }, 5000);

        setLocalToast({
          variant: "success",
          content: `Attachment ${fileName} was successfully downloaded`,
          nonce: Date.now(),
        });
      } else {
        setIsDownloadedPreview(false);
        setLocalToast({
          variant: "error",
          content: `Failed to download attachment ${fileName}`,
          nonce: Date.now(),
        });
      }
    } catch (error: any) {
      console.log("onDownloadPreview error:", error?.message);
      setIsDownloadedPreview(false);
      setLocalToast({
        variant: "error",
        content: `Failed to download attachment ${fileName}`,
        nonce: Date.now(),
      });
    } finally {
      setIsPreviewLoading(false);
      setIsDownloadingAny(false);
    }
  };

  return (
    <div
      className="bg-bgSecondary h-[var(--shell-vh)] relative overflow-y-auto pt-[70px]"
      data-testid={tid("preview")}
      aria-label="Document preview"
    >
      <div
        className="lg:px-10 px-5 py-5 bg-bgPrimary flex items-center justify-between fixed top-0 w-full z-10"
        data-testid={tid("preview-header")}
      >
        <div className="flex items-center gap-2">
          <EyeIcon
            aria-hidden="true"
            strokeColor="var(--color-txt-primary)"
            width="24"
            height="24"
          />
          <div className="lg:text-[26px] text-[22px] text-txtPrimary font-medium leading-[30px]">
            Preview
          </div>
        </div>
        <div className="flex items-center gap-5">
          <button
            className={`flex items-center gap-2 ${
              isDownloadingAny
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer"
            }`}
            onClick={() => onDownloadHeader(data?.title, data?.newTitle)}
            disabled={isDownloadingAny}
            data-testid={tid("preview-download")}
            aria-label="Download document"
          >
            {isHeaderLoading ? (
              <div
                className="h-4 w-4 border-2 border-blue border-t-transparent rounded-full animate-spin"
                aria-hidden="true"
              />
            ) : (
              <DownloadIcon
                aria-hidden="true"
                strokeColor={
                  isDownloadingAny
                    ? "var(--color-txt-secondary)"
                    : "var(--color-btn-primary-active)"
                }
                width="16"
                height="16"
              />
            )}

            <div className="text-sm font-medium text-btnPrimaryActive hover:!text-btnPrimaryHover leading-normal underline">
              {isHeaderLoading ? "Downloading..." : "Download"}
            </div>
          </button>
          <div
            onClick={() => onClose()}
            className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
            role="button"
            tabIndex={0}
            data-testid={tid("preview-close")}
            aria-label="Close document preview"
          >
            <CloseIcon aria-hidden="true" />
          </div>
        </div>
      </div>

      <div
        className="lg:px-10 px-5 pt-[30px] h-full flex justify-center items-center"
        data-testid={tid("preview-content")}
        aria-label="Document preview content"
      >
        {isLoading ? (
          <div
            className="h-full flex justify-center items-center"
            data-testid={tid("preview-loading")}
            aria-label="Loading document preview"
          >
            <div
              className="inline-block h-16 w-16 animate-spin rounded-full border-[5px] border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"
              aria-hidden="true"
            />
          </div>
        ) : url ? (
          data?.doc_type === "image" ? (
            <img
              src={url}
              alt={data?.newTitle || "Document preview"}
              data-testid={tid("image-preview")}
            />
          ) : data?.doc_type === "audio" ? (
            <div
              data-testid={tid("audio-preview")}
              aria-label="Audio document preview"
            >
              <AudioPreview
                audioUrl={url}
                docTitle={data?.title}
                newTitle={data?.newTitle}
                id={isPreviewId}
                onDownloadClick={onDownloadPreview}
                isDownloading={isDownloadingAny}
                isPreviewLoading={isPreviewLoading}
                isDownloaded={isDownloadedPreview}
              />
            </div>
          ) : (
            <div
              data-testid={tid("file-preview")}
              aria-label="File document preview"
            >
              <DocPreview
                docUrl={url}
                onDownloadClick={onDownloadPreview}
                isDownloading={isDownloadingAny}
                isPreviewLoading={isPreviewLoading}
                docTitle={data?.title}
                newTitle={data?.newTitle}
                id={isPreviewId}
                isDownloadedPreview={isDownloadedPreview}
              />
            </div>
          )
        ) : null}
      </div>

      {/* Local Toast Message */}
      <MessageToast
        key={localToast.nonce || "preview-toast"}
        variant={localToast.variant as any}
        content={localToast.content}
        isProgressBar
        width="max-w-[810px] px-5 w-full"
        className="!right-1/2 !translate-x-1/2 !bottom-[80px]"
        onCloseClick={() =>
          setLocalToast({ variant: "", content: "", nonce: 0 })
        }
      />
    </div>
  );
};

export default PreviewDocuments;
